import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLang } from '../hooks/useLang';
import LanguageSwitcher from './LanguageSwitcher';
import './Navbar.css';

const COMING_SOON = process.env.REACT_APP_COMING_SOON === 'true';

export default function Navbar() {
  const { user, profile, signOut } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  async function handleSignOut() {
    await signOut();
    setMenuOpen(false);
    navigate('/');
  }

  function closeMenu() { setMenuOpen(false); }

  const isSender      = profile?.role === 'sender'      || profile?.role === 'both';
  const isTransporter = profile?.role === 'transporter' || profile?.role === 'both';
  const isAdmin       = profile?.is_admin === true;
  const isBeta        = profile?.is_beta_tester === true;
  const hasAppAccess  = isAdmin || isBeta;

  return (
    <>
      <nav className="navbar">
        <div className="container navbar-inner">
          <Link to="/" className="nav-logo" onClick={closeMenu}>
            Transport<span>ily</span>
          </Link>

          <div className="nav-links">
            {(!COMING_SOON || hasAppAccess) && isSender && (
              <Link to="/search" className={'nav-link' + (location.pathname === '/search' ? ' active' : '')}>
                {t('nav.findTransporters')}
              </Link>
            )}
            {(!COMING_SOON || hasAppAccess) && isTransporter && (
              <Link to="/post-trip" className={'nav-link' + (location.pathname === '/post-trip' ? ' active' : '')}>
                {t('nav.postTrip')}
              </Link>
            )}
            {(!COMING_SOON || hasAppAccess) && user && (
              <Link to="/dashboard" className={'nav-link' + (location.pathname === '/dashboard' ? ' active' : '')}>
                {t('nav.dashboard')}
              </Link>
            )}
            {(!COMING_SOON || hasAppAccess) && user && (
              <Link to="/requests" className={'nav-link' + (location.pathname === '/requests' ? ' active' : '')}>
                {isTransporter && !isSender ? t('nav.findRequests') : t('nav.requests')}
              </Link>
            )}
            <Link to="/customs" className={'nav-link' + (location.pathname === '/customs' ? ' active' : '')}>
              {t('nav.customsGuide')}
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                className={'nav-link' + (location.pathname === '/admin' ? ' active' : '')}
                title="Admin Dashboard"
                style={{ opacity: 0.45, fontSize: '12px', letterSpacing: '0.05em' }}
              >
                ⚙️
              </Link>
            )}
          </div>

          <div className="nav-actions">
            <div className="nav-lang-desktop">
              <LanguageSwitcher />
            </div>
            {user ? (
              <>
                {(!COMING_SOON || hasAppAccess) && (
                  <Link to="/profile" className="nav-avatar" onClick={closeMenu}>
                    <div className="avatar-circle">{profile?.full_name?.[0] || '?'}</div>
                    <span className="nav-name">{profile?.full_name?.split(' ')[0]}</span>
                  </Link>
                )}
                <button onClick={handleSignOut} className="btn btn-ghost btn-sm nav-signout">
                  {t('nav.signOut')}
                </button>
              </>
            ) : COMING_SOON ? (
              <a href="#signup" className="btn btn-primary btn-sm" onClick={closeMenu}>
                🚀 Join Waitlist
              </a>
            ) : (
              <>
                <Link to="/login"    className="btn btn-ghost btn-sm">{t('nav.logIn')}</Link>
                <Link to="/register" className="btn btn-primary btn-sm">{t('nav.joinFree')}</Link>
              </>
            )}
            <button
              className={'hamburger' + (menuOpen ? ' open' : '')}
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {menuOpen && (
        <div className="mobile-menu">
          {(!COMING_SOON || hasAppAccess) && isSender      && <Link to="/search"    className="mobile-link" onClick={closeMenu}>🔍 {t('nav.findTransporters')}</Link>}
          {(!COMING_SOON || hasAppAccess) && isTransporter  && <Link to="/post-trip" className="mobile-link" onClick={closeMenu}>✈️ {t('nav.postTrip')}</Link>}
          {(!COMING_SOON || hasAppAccess) && user           && <Link to="/dashboard" className="mobile-link" onClick={closeMenu}>📊 {t('nav.dashboard')}</Link>}
          {(!COMING_SOON || hasAppAccess) && user           && <Link to="/requests"  className="mobile-link" onClick={closeMenu}>📋 {isTransporter && !isSender ? t('nav.findRequests') : t('nav.requests')}</Link>}
          {(!COMING_SOON || hasAppAccess) && user           && <Link to="/profile"   className="mobile-link" onClick={closeMenu}>👤 Profile</Link>}
          <Link to="/customs" className="mobile-link" onClick={closeMenu}>🛃 {t('nav.customsGuide')}</Link>
          {isAdmin && <Link to="/admin" className="mobile-link" onClick={closeMenu} style={{ opacity: 0.5 }}>⚙️ Admin</Link>}
          <div className="mobile-divider" />
          <div style={{ padding: '12px 20px' }}>
            <LanguageSwitcher />
          </div>
          <div className="mobile-divider" />
          {user ? (
            <button onClick={handleSignOut} className="mobile-link mobile-signout">{t('nav.signOut')}</button>
          ) : COMING_SOON ? (
            <a href="#signup" className="mobile-link mobile-join" onClick={closeMenu}>🚀 Join Waitlist →</a>
          ) : (
            <>
              <Link to="/login"    className="mobile-link"            onClick={closeMenu}>{t('nav.logIn')}</Link>
              <Link to="/register" className="mobile-link mobile-join" onClick={closeMenu}>{t('nav.joinFree')} →</Link>
            </>
          )}
        </div>
      )}

      {menuOpen && <div className="mobile-overlay" onClick={closeMenu} />}
    </>
  );
}